import PageLoader from "@/components/loader/PageLoader";
import WarningBanner from "@/components/banners/WarningBanner";
import { useUser } from "@/contexts/UserContext";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import { faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface AddDeviceProps {
  onMenuItemClick: (componentName: string, successMessage?: string) => void;
  setSuccessMessage: (message: string) => void;
}
interface Category {
  id: number;
  name: string;
}
interface Product {
  id: number;
  name: string;
}

const AddDevice = ({ onMenuItemClick, setSuccessMessage }: AddDeviceProps) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const selectedBranch = user?.selectedBranch;
  const [axiosInstance, loading, isFetching] = useAxiosInstance();

  const [name, setName] = useState("");
  const [deviceNumber, setDeviceNumber] = useState("");
  const [status, setStatus] = useState("Active");
  const [mode, setMode] = useState("Online");
  const [isPrimary, setIsPrimary] = useState(false);

  // category and product selections for device
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  const [categorySearchQuery, setCategorySearchQuery] = useState("");
  const [productSearchQuery, setProductSearchQuery] = useState("");
  const [showBanner, setShowBanner] = useState(false);
  const [bannerMessage, setBannerMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [animateOut, setAnimateOut] = useState(false);

  const apiUrl = `${import.meta.env.VITE_APP_API_BASE_URL}${
    import.meta.env.VITE_APP_REDBIRDPOSBE_DEVICES
  }`;

  useEffect(() => {
    const fetchAllCategories = async () => {
      try {
        if (!loading && axiosInstance && user?.selectedBranch) {
          const apiUrl = `${import.meta.env.VITE_APP_API_BASE_URL}${
            import.meta.env.VITE_APP_REDBIRDPOSBE_CATEGORY_INFORMATION
          }`;
          let allFetchedCategories: Category[] = [];
          let nextUrl = `${apiUrl}?branch=${user.selectedBranch.id}`;

          if (categorySearchQuery) {
            nextUrl += `&name=${encodeURIComponent(categorySearchQuery)}`;
          }

          while (nextUrl) {
            const response = await axiosInstance.get(nextUrl);
            const { results, next } = response.data;
            allFetchedCategories = [...allFetchedCategories, ...results];
            nextUrl = next;
          }

          setAllCategories(allFetchedCategories);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    const fetchAllProducts = async () => {
      try {
        if (!loading && axiosInstance && user?.selectedBranch) {
          const apiUrl = `${import.meta.env.VITE_APP_API_BASE_URL}${
            import.meta.env.VITE_APP_REDBIRDPOSBE_PRODUCT_INFORMATION
          }`;
          let nextUrl = `${apiUrl}?branch=${user.selectedBranch.id}`;

          if (productSearchQuery) {
            nextUrl += `&menu_display_name=${encodeURIComponent(
              productSearchQuery,
            )}`;
          }

          const response = await axiosInstance.get(nextUrl);
          const { results } = response.data;
          setAllProducts(results);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };

    fetchAllCategories();
    fetchAllProducts();
  }, [
    axiosInstance,
    loading,
    user?.selectedBranch,
    categorySearchQuery,
    productSearchQuery,
  ]);

  const handleCategoryChange = (selectedCategoryId: number) => {
    setSelectedCategories((prevSelected) => {
      const categories = prevSelected.includes(selectedCategoryId)
        ? prevSelected.filter((id) => id !== selectedCategoryId)
        : [...prevSelected, selectedCategoryId];

      return categories;
    });
  };

  const handleProductChange = (selectedProductId: number) => {
    setSelectedProducts((prevSelected) => {
      const products = prevSelected.includes(selectedProductId)
        ? prevSelected.filter((id) => id !== selectedProductId)
        : [...prevSelected, selectedProductId];

      return products;
    });
  };

  const handleRemoveCategory = (categoryId: number) => {
    setSelectedCategories((prevSelected) =>
      prevSelected.filter((id) => id !== categoryId),
    );
  };

  const handleRemoveProduct = (productId: number) => {
    setSelectedProducts((prevSelected) =>
      prevSelected.filter((id) => id !== productId),
    );
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const payload = {
      name,
      device_number: deviceNumber,
      branch: selectedBranch?.id,
      status,
      mode,
      is_primary: isPrimary,
      categories: selectedCategories,
      products: selectedProducts,
    };

    try {
      if (!loading && axiosInstance) {
        const response = await axiosInstance.post(apiUrl, payload, {
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (response.status === 201) {
          const successMessage = t(
            "back.management.business.device.successAdd",
          );
          setSuccessMessage(successMessage);
          setIsSuccess(true);
          setBannerMessage(successMessage);
          setShowBanner(true);
          setTimeout(() => onMenuItemClick("devices", successMessage), 3000); // Delay to show success message
        } else {
          const errorMessage = t("back.management.business.device.failAdd");
          setIsSuccess(false);
          setBannerMessage(errorMessage);
          setShowBanner(true);
        }
      }
    } catch (error) {
      console.error("Error adding device", error);
      setIsSuccess(false);
      setBannerMessage(t("back.management.business.device.failAdd"));
      setShowBanner(true);
    }
  };

  const debouncedCategorySearch = useCallback(
    _.debounce((query: string) => setCategorySearchQuery(query), 300),
    [],
  );

  const debouncedProductSearch = useCallback(
    _.debounce((query: string) => setProductSearchQuery(query), 300),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedCategorySearch.cancel();
      debouncedProductSearch.cancel();
    };
  }, [debouncedCategorySearch, debouncedProductSearch]);

  const handleCategorySearchChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    debouncedCategorySearch(e.target.value);
  };

  const handleProductSearchChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    debouncedProductSearch(e.target.value);
  };

  useEffect(() => {
    if (showBanner) {
      setAnimateOut(false);
      const timerId = setTimeout(() => {
        setAnimateOut(true);
        setTimeout(() => setShowBanner(false), 500);
      }, 3000);

      return () => clearTimeout(timerId);
    }
  }, [showBanner]);

  return (
    <PageLoader isFetching={isFetching}>
      <div className="container mx-auto px-4 py-2">
        {showBanner && (
          <WarningBanner
            title={isSuccess ? "Success" : "Error"}
            text={bannerMessage}
            isSuccess={isSuccess}
            className={`${
              animateOut ? "animate-slideOutRight" : "animate-slideDown"
            }`}
          />
        )}
        <h1 className="text-xl font-bold">
          {t("back.management.business.device.addNewDevice")}
        </h1>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div>
            <label
              htmlFor="name"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              {t("back.management.business.device.name")}
            </label>
            <input
              id="name"
              name="name"
              type="text"
              required
              className="relative block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-red-500 focus:outline-none focus:ring-red-500 sm:text-sm"
              placeholder={t("back.management.business.device.enterName")}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <label
              htmlFor="device_number"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              {t("back.management.business.device.deviceNumber")}
            </label>
            <input
              id="device_number"
              name="device_number"
              type="text"
              required
              className="relative block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-red-500 focus:outline-none focus:ring-red-500 sm:text-sm"
              placeholder={t(
                "back.management.business.device.enterDeviceNumber",
              )}
              value={deviceNumber}
              onChange={(e) => setDeviceNumber(e.target.value)}
            />
          </div>
          <div>
            <label
              htmlFor="status"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              {t("back.management.business.device.status")}
            </label>
            <select
              id="status"
              name="status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              required
              className="relative block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-red-500 focus:outline-none focus:ring-red-500 sm:text-sm"
            >
              <option value="Active">
                {t("back.management.business.device.active")}
              </option>
              <option value="Inactive">
                {t("back.management.business.device.inactive")}
              </option>
            </select>
          </div>
          <div>
            <label
              htmlFor="mode"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              {t("back.management.business.device.mode")}
            </label>
            <select
              id="mode"
              name="mode"
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              required
              className="relative block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-red-500 focus:outline-none focus:ring-red-500 sm:text-sm"
            >
              <option value="Offline">
                {t("back.management.business.device.offline")}
              </option>
              <option value="Online">
                {t("back.management.business.device.online")}
              </option>
            </select>
          </div>
          <div>
            <label
              htmlFor="categories"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              {t("back.management.business.device.categories")}
            </label>
            <input
              type="text"
              placeholder={t(
                "back.management.business.device.searchCategories",
              )}
              onChange={handleCategorySearchChange}
              className="relative mb-2 block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-red-500 focus:outline-none focus:ring-red-500 sm:text-sm"
            />
            <div className="mt-4">
              <p className="mb-2 text-sm font-bold text-gray-700">
                {t("back.management.business.device.selectedCategories")}
              </p>
              <ul>
                {allCategories
                  .filter((category) =>
                    selectedCategories.includes(category.id),
                  )
                  .map((category) => (
                    <li key={category.id} className="mb-2 flex items-center">
                      <span className="mr-2 text-gray-700">
                        {category.name}
                      </span>
                      <button
                        onClick={() => handleRemoveCategory(category.id)}
                        className="text-red-500 hover:text-red-700 focus:outline-none"
                      >
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </button>
                    </li>
                  ))}
              </ul>
            </div>
            <select
              id="categories"
              multiple
              value={selectedCategories.map(String)}
              onChange={(e) =>
                handleCategoryChange(parseInt(e.target.value, 10))
              }
              className="relative block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-red-500 focus:outline-none focus:ring-red-500 sm:text-sm"
            >
              {allCategories
                .filter((category) => !selectedCategories.includes(category.id))
                .map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label
              htmlFor="products"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              {t("back.management.business.device.products")}
            </label>
            <input
              type="text"
              placeholder={t("back.management.business.device.searchProducts")}
              onChange={handleProductSearchChange}
              className="relative mb-2 block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-red-500 focus:outline-none focus:ring-red-500 sm:text-sm"
            />
            <div className="mt-4">
              <p className="mb-2 text-sm font-bold text-gray-700">
                {t("back.management.business.device.selectedProducts")}
              </p>
              <ul>
                {allProducts
                  .filter((product) => selectedProducts.includes(product.id))
                  .map((product) => (
                    <li key={product.id} className="mb-2 flex items-center">
                      <span className="mr-2 text-gray-700">{product.name}</span>
                      <button
                        onClick={() => handleRemoveProduct(product.id)}
                        className="text-red-500 hover:text-red-700 focus:outline-none"
                      >
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </button>
                    </li>
                  ))}
              </ul>
            </div>
            <select
              id="products"
              multiple
              value={selectedProducts.map(String)}
              onChange={(e) =>
                handleProductChange(parseInt(e.target.value, 10))
              }
              className="relative block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-red-500 focus:outline-none focus:ring-red-500 sm:text-sm"
            >
              {allProducts
                .filter((product) => !selectedProducts.includes(product.id))
                .map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label
              htmlFor="is_primary"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              {t("back.management.business.device.isPrimary")}
            </label>
            <input
              id="is_primary"
              name="is_primary"
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
              checked={isPrimary}
              onChange={(e) => setIsPrimary(e.target.checked)}
            />
          </div>
          <div>
            <button
              type="submit"
              className="group relative flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              {t("back.management.business.device.addDevice")}
            </button>
          </div>
        </form>
        <div className="mt-4 flex justify-end">
          <button
            onClick={() => onMenuItemClick("devices")}
            className="mr-2 rounded-md bg-gray-500 px-4 py-2 text-white"
          >
            {t("back.management.menu.product.cancelbutton")}
          </button>
        </div>
      </div>
    </PageLoader>
  );
};

export default AddDevice;
